// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  CREDENTIAL_API_URL,
  REGISTRY_API_URL,
  REWARD_DISTRIBUTION_API_URL,
} from '../config/externalApis';
import { LoggedInUser } from '../contexts/UserContext';

export type DeadTask<T> = {
  id: string;
  task: T;
  reason: string;
};

export enum UtilityBackend {
  CREDENTIAL,
  REGISTRY,
  REWARD_DISTRIBUTION,
}

const utilityBackends = new Map([
  [UtilityBackend.CREDENTIAL, CREDENTIAL_API_URL],
  [UtilityBackend.REGISTRY, REGISTRY_API_URL],
  [UtilityBackend.REWARD_DISTRIBUTION, REWARD_DISTRIBUTION_API_URL],
]);

const getDeadTaskUrl = (
  backend: UtilityBackend,
  deadTaskApiPath: string,
  triggerName?: string,
  taskId?: string,
) => {
  const backendUrl = utilityBackends.get(backend);
  if (!backendUrl) {
    throw new Error(`Dead task client - Utility backend '${backend}' not found`);
  }
  return [backendUrl, deadTaskApiPath, triggerName, taskId].filter(Boolean).join('/');
};
const getUnresponsivePartiesUrl = (backend: UtilityBackend) => {
  const backendUrl = utilityBackends.get(backend);
  if (!backendUrl) {
    throw new Error(`Utility backend '${backend}' not found`);
  }
  return [backendUrl, 'unresponsive-parties'].filter(Boolean).join('/');
};

export default class UtilityAutomationApiClient {
  private readonly loggedInUser: LoggedInUser;

  private readonly deadTaskApiPath = 'dead-tasks';

  constructor(loggedInUser: LoggedInUser) {
    this.loggedInUser = loggedInUser;
  }

  async triggers(backend: UtilityBackend): Promise<string[]> {
    try {
      // TODO: Do these request via a library that automatically handles OAuth2.
      const url = new URL(getDeadTaskUrl(backend, this.deadTaskApiPath));
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${this.loggedInUser.userAccessToken}`,
        },
      });
      return (await response.json()) as string[];
    } catch (error) {
      console.error('Error fetching triggers:', error);
      return Promise.reject(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async deadTasks(backend: UtilityBackend, triggerName: string): Promise<DeadTask<any>[]> {
    try {
      const url = new URL(getDeadTaskUrl(backend, this.deadTaskApiPath, triggerName));
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${this.loggedInUser.userAccessToken}`,
        },
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (await response.json()) as DeadTask<any>[];
    } catch (error) {
      console.error('Error fetching dead tasks:', error);
      return Promise.reject(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async unresponsiveParties(backend: UtilityBackend): Promise<string[]> {
    try {
      const url = new URL(getUnresponsivePartiesUrl(backend));
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${this.loggedInUser.userAccessToken}`,
        },
      });

      return (await response.json()) as string[];
    } catch (error) {
      console.error('Error fetching unresponsive parties:', error);
      return Promise.reject(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async deleteUnresponsiveParties(
    backend: UtilityBackend,
    unresponsiveParties: string[],
  ): Promise<void> {
    try {
      const url = new URL(getUnresponsivePartiesUrl(backend));
      await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.loggedInUser.userAccessToken}`,
        },
        body: JSON.stringify(unresponsiveParties),
      });
    } catch (error) {
      console.error('Error deleting unresponsive parties', error);
    }
  }

  async deleteTask(backend: UtilityBackend, triggerName: string, taskId: string): Promise<void> {
    try {
      const url = new URL(getDeadTaskUrl(backend, this.deadTaskApiPath, triggerName, taskId));
      await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${this.loggedInUser.userAccessToken}`,
        },
      });
    } catch (error) {
      console.error('Error deleting dead task:', error);
    }
  }
}
